import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheetRef } from 'react-spring-bottom-sheet';

import { FilterModelIcon } from '../../assets';
import { useFilterContext } from '../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { ModelFilter } from '../../models/Filter';
import BaseFilterComponent from './BaseFilterComponent';
import { StyledButton, StyledSecondaryButton, StyledShimmerTitle } from './FiltersList.styles';
import { useSelectionFilter } from './hooks/SelectionFilter';
import SelectionFilter, { Value } from './SelectionFilter/SelectionFilter';
import { FilterFooterMobile } from './shared/FilterFooterMobile.styles';

interface AddModelFilterProps {
  keyParentValue: string;
  sheetRef?: React.MutableRefObject<BottomSheetRef>;
}

export const AddModelFilterComponent = ({ keyParentValue, sheetRef }: AddModelFilterProps) => {
  const {
    getSelectedModelFilterByKey,
    getModelFiltersCount,
    handleApplyFilter,
    setOpenFiltersMobile,
    setSelectedFilterTitle,
  } = useFilterContext();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);
  const filter = getSelectedModelFilterByKey(keyParentValue) as ModelFilter;

  const { getFilter, optionValues, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (models, keyParentValue) => new ModelFilter({ values: models, parentKeyValue: keyParentValue }),
    extra: keyParentValue,
  });
  const [isModelCountLoading, setIsModelCountLoading] = React.useState(false);

  React.useEffect(() => {
    void (async () => {
      try {
        setIsModelCountLoading(true);
        const fetchedFiltersCount = await getModelFiltersCount(keyParentValue);
        if (fetchedFiltersCount) {
          if (fetchedFiltersCount.length === 0) {
            //This timeout will adjust the height of bottomsheet when passing from shimmers to no option view
            setTimeout(() => {
              sheetRef.current?.snapTo(0);
            }, 100);
          }
          setArrOptions(
            fetchedFiltersCount.map((filter) => ({
              selected: filter.isSelected,
              value: filter.key,
              label: filter.key,
              data: filter.count,
            })) || [],
          );
        }
      } catch (error: any) {
        setArrOptions([]);
      } finally {
        setIsModelCountLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();

    handleApplyFilter(filter);

    setTimeout(() => {
      setOpenFiltersMobile(false);
      setSelectedFilterTitle('');
    }, 100);
  };

  return (
    <>
      <SelectionFilter
        values={optionValues}
        onChange={onChangeMultiSelectionFnc}
        multiselect
        loading={isModelCountLoading}
        searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'models' })}
        displayCount={!inMobile(width)}
        reverseCheckboxOrder={inMobile(width)}
        checkboxSizeInPx={inMobile(width) ? 24 : undefined}
      />
      {isModelCountLoading ? (
        <FilterFooterMobile>
          <StyledShimmerTitle $flex="1" $height="3rem" $borderRadius="2.125rem" />
          <StyledShimmerTitle $flex="1" $height="3rem" $borderRadius="2.125rem" />
        </FilterFooterMobile>
      ) : (
        <FilterFooterMobile>
          <StyledSecondaryButton onPress={clearAllSelected}>{t('filters.clearAll')}</StyledSecondaryButton>
          <StyledButton disabled={arrOptions?.length === 0} onPress={applyOnClick}>
            {t('filters.applyFilter')}
          </StyledButton>
        </FilterFooterMobile>
      )}
    </>
  );
};

const AddModelFilter = ({ keyParentValue }: AddModelFilterProps) => {
  const { getSelectedModelFilterByKey, getModelFiltersCount } = useFilterContext();
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  const [arrOptions, setArrOptions] = React.useState<Value[]>(null);
  const filter = getSelectedModelFilterByKey(keyParentValue) as ModelFilter;

  const { getFilter, optionValues, onCancel, onChangeMultiSelectionFnc, clearAllSelected } = useSelectionFilter({
    arrOptions,
    filter,
    constructor: (models, keyParentValue) => new ModelFilter({ values: models, parentKeyValue: keyParentValue }),
    extra: keyParentValue,
  });
  const [isModelCountLoading, setIsModelCountLoading] = React.useState(false);

  const filterTitle = `${keyParentValue?.[0].toUpperCase() + keyParentValue.slice(1)} ${t('filters.models')}`;

  const handleOnOpenModal = async () => {
    try {
      setIsModelCountLoading(true);
      const fetchedFiltersCount = await getModelFiltersCount(keyParentValue);
      if (fetchedFiltersCount) {
        setArrOptions(
          fetchedFiltersCount.map((filter) => ({
            selected: filter.isSelected,
            value: filter.key,
            label: filter.key,
            data: filter.count,
          })) || [],
        );
      }
    } catch (error: any) {
      setArrOptions([]);
    } finally {
      setIsModelCountLoading(false);
    }
  };

  return (
    <>
      <BaseFilterComponent
        getFilter={getFilter}
        icon={<FilterModelIcon />}
        title={filterTitle}
        onOpen={handleOnOpenModal}
        onCancel={onCancel}
        onClearAll={clearAllSelected}
        inUse={!!filter}
        storedFilter={filter}
        disableFilterButton={arrOptions?.length === 0}
        loading={isModelCountLoading}
      >
        <SelectionFilter
          values={optionValues}
          onChange={onChangeMultiSelectionFnc}
          multiselect
          loading={isModelCountLoading}
          searchInputPlaceholder={t('filters.searchFilterPlaceholder', { param: 'models' })}
          displayCount={!inMobile(width)}
          reverseCheckboxOrder={inMobile(width)}
          checkboxSizeInPx={inMobile(width) ? 24 : undefined}
        />
      </BaseFilterComponent>
    </>
  );
};

export default AddModelFilter;
